import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef
} from '@angular/core';
import { PtgInterface } from '../../../model/ptgs/ptg-interface';
import { HostListener } from '@angular/core';
import { PtgVersionInterface } from '../../../model/ptgs/ptg-version-interface';
import { VariantVersionInterface } from '../../../model/products/variant-version-interface';
import { ProductService } from 'src/app/services/dataServices/product-service/productService';
import { PtgService } from 'src/app/services/dataServices/ptg-service/ptgService';
import { AttributeValidationServiceService } from 'src/app/services/dataServices/dataValidation/attribute-validation-service/attribute-validation-service.service';
import { CertificationVariantVersionInterface } from 'src/app/model/certifications/certification-variant-version-interface';
import { CertificationService } from 'src/app/services/dataServices/certification-service/certificationService';
import { SortMethod } from 'src/app/model/enums/SortMethod';
import { PtgPageInterface } from 'src/app/model/ptgs/ptg-page-interface';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute } from '@angular/router';
import { CategoryService } from 'src/app/services/dataServices/product-service/categoryService';

@Component({
    selector: 'app-add-ptg-dependency-table',
    templateUrl: './add-ptg-dependency-table.component.html',
    styleUrls: ['./add-ptg-dependency-table.component.scss'],
    standalone: false
})
export class AddPtgDependencyTableComponent implements OnInit {
  @Input() version: VariantVersionInterface | CertificationVariantVersionInterface;
  @Input() isCertification?: boolean;
  @Input() selectedCategory?: string;

  @Output() addedPtgVersion = new EventEmitter<PtgVersionInterface>();
  @Output() removedPtgVersion = new EventEmitter<PtgVersionInterface>();

  public addPtgDependencyMode: boolean = false;
  public ptgWithActiveVersions: PtgPageInterface;
  public totalNumberOfElements: number;
  public pageIndex: number = 0;
  public pageSize: number = 24;
  private productId: number;
  private productCategoryName: string | undefined;

  constructor(
    public ptgService: PtgService,
    public productService: ProductService,
    public certificationService: CertificationService,
    private cd: ChangeDetectorRef,
    private attributeValidationService: AttributeValidationServiceService,
    private route: ActivatedRoute,
    private categoryService: CategoryService
  ) { }

  async ngOnInit(): Promise<void> {
    this.updatePageSize();
  }

  async ngOnChanges(): Promise<void> {
    if (this.selectedCategory) {
      this.productCategoryName = this.selectedCategory;
    } else {
      this.productId = Number(this.route.snapshot.paramMap.get('productId'));
      const categories = await this.categoryService.loadCategories();
      const product = await this.productService.getProduct(this.productId);
      const productCategory = categories.find(c => c.value === product.category);
      this.productCategoryName = productCategory?.name;
    }
    this.pageIndex = 0;
    this.loadPTGPage();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.updatePageSize();
  }

  onToggle(isAddPTGDependencyMode: boolean) {
    this.addPtgDependencyMode = isAddPTGDependencyMode;
    if (this.addPtgDependencyMode && !this.cd['destroyed']) {
      this.cd.detectChanges();
    }
  }

  pageChanged(event: PageEvent): void {
    this.pageIndex = event.pageIndex * this.pageSize;
    this.loadPTGPage();
  }

  private loadPTGPage(): void {
    this.ptgService
      .getPtgSliceActVer(
        this.pageIndex,
        this.pageSize,
        "",
        SortMethod.ALPHABETICAL_ASC,
        this.productCategoryName ? this.productCategoryName : '',
      )
      .subscribe((ptgPage: PtgPageInterface) => {
        const allPtgs = ptgPage?.ptgList ?? [];

        const validPtgs = allPtgs.filter((ptg) => {
          if (!ptg || !ptg.versions || ptg.versions.length === 0) {
            return false;
          }

          const activeVersion = ptg.versions
            .slice()
            .sort((a, b) => b.number - a.number)[0];

          if (activeVersion.status !== 'ACTIVE') {
            return false;
          }

          ptg.activeVersion = activeVersion;

          // if (
          //   !activeVersion.attributeValidations ||
          //   activeVersion.attributeValidations.length === 0
          // ) {
          //   return false;
          // }

          return true;
        });

        this.ptgWithActiveVersions = {
          ...ptgPage,
          ptgList: validPtgs,
          totalNumberOfElements: ptgPage.totalNumberOfElements,
        };
        this.cd.detectChanges();
      });
  }

  addPtgVersion(ptg: PtgInterface): void {
    // const newVersion: PtgVersionInterface = ptg.versions[0];
    const newVersion: PtgVersionInterface = ptg.activeVersion;
    const containsSelectedVersion = this.version.ptgVersions.some(
      (version) => {
        return version.id == newVersion.id;
      }
    );
    if (containsSelectedVersion) {
      return;
    }

    this.version.ptgVersions.push(newVersion);
    this.version.ptgVersionIds.push(newVersion.id);

    this.updatePtgVersionOrderInDescriptions();

    newVersion.attributeValidations.forEach((attributeValidation) => {
      const attribute = attributeValidation.attribute;
      if (
        !this.attributeValidationService.activeEntityFeatureValues.has(
          attribute.id
        )
      ) {
        this.attributeValidationService.activeEntityFeatureValues.set(
          attribute.id,
          null
        );
      }
    });

    this.addedPtgVersion.emit(newVersion);
  }

  togglePtgVersion(ptg: PtgInterface, checked: boolean): void {
    if (checked) {
      if (this.version.ptgVersions.length >= 3) {
        console.warn('Maximal 3 Produkttypen können ausgewählt werden');
        return;
      }
      this.addPtgVersion(ptg);
    } else {
      this.removePtgVersion(ptg);
    }
  }

  removePtgVersion(ptg: PtgInterface): void {
    // const versionToRemove = ptg.versions[0];
    const versionToRemove = ptg.activeVersion;
    this.version.ptgVersions = this.version.ptgVersions.filter(
      (v) => v.id !== versionToRemove.id
    );
    this.version.ptgVersionIds = this.version.ptgVersionIds.filter(
      (id) => id !== versionToRemove.id
    );

    this.updatePtgVersionOrderInDescriptions();
    this.removedPtgVersion.emit(versionToRemove);
  }

  isPtgSelected(ptg: PtgInterface): boolean {
    if (!ptg || !ptg.versions || ptg.versions.length === 0) {
      return false;
    }

    // const versionToCheck = ptg.versions[0];
    const versionToCheck = ptg.activeVersion;

    if (!versionToCheck || !versionToCheck.id) {
      return false;
    }

    if (!this.version?.ptgVersions || this.version.ptgVersions.length === 0) {
      return false;
    }

    return this.version.ptgVersions.some((v) => v?.id === versionToCheck.id);
  }


  private updatePageSize() {
    const width = window.innerWidth;
    if (width <= 768) {
      this.pageSize = 16;
    } else {
      this.pageSize = 24;
    }
    this.pageIndex = 0;
  }


  private updatePtgVersionOrderInDescriptions(): void {
    this.version.ptgVersions.forEach((v, i) => {
      v.description = v.description?.replace(/\s*<<>>\d+,\d+$/, '') || '';
      v.description = `${v.description}<<>>${v.id},${i + 1}`;
    });
  }
}
